import React from "react";
import { Input } from "../../../../../../common/Input";
import { Select } from "../../../../../../common/Select";
import { EmployeePickerField } from "../../../../../../common/selectComponents/EmployeePicker";
import { MedicalClearancePicker } from "../../../../../../common/selectComponents/MedicalClearancePicker";
import SectionBadge from "../../ui/SectionBadge";
import EmployeeCard from "../../ui/EmployeeCard";
import PositionPickerField from "../../../../../../common/selectComponents/PositionPicker";
import DepartmentPickerField from "../../../../../../common/selectComponents/DepartmentPicker";
import VesselPickerField from "../../../../../../common/selectComponents/VesselPicker";

const EmployeeInfoTab = ({ form, set, options, editable, errors }) => {
  const handleSelectEmployee = (emp) => {
    set('assignedEmployeePersonalInformationId', emp.id);
    set('employeeData', { fullName: emp.fullName, employeeId: emp.employeeId, position: emp.employeeInformationPositionDescription || "N/A", department: emp.employeeInformationDepartmentDescription || "N/A" });
    set('autoPopulated', {
      employeeType: emp.employeeInformationEmployeeTypeDescription || "",
      destination: emp.employeeInformationDestinationDescription || "",
      regularTitle: emp.employeeInformationPositionDescription || "",
      regularVessel: emp.employeeInformationVesselName || "",
      vesselType: emp.employeeInformationVesselTypeName || "",
      viaCaptain: "",
    });
  };

  const handleClearPosition = () => {
    set('tempPositionId', "");
    set('tempPositionName', "");
  }

  return (
    <div className="space-y-6 pt-2">
      <div className="rounded-xl border border-gray-200 bg-gray-50 p-6">
        <SectionBadge label="Employee Assignment" editable={editable} />
        <div className="grid grid-cols-1 gap-x-10 gap-y-4 lg:grid-cols-2">
          <div>
            <EmployeePickerField
              label="Employee Name:"
              employeePersonalInformationId={form.assignedEmployeePersonalInformationId}
              displayValue={form.employeeData ? `${form.employeeData.employeeId} - ${form.employeeData.fullName}` : ""}
              onSelect={handleSelectEmployee}
              onClear={() => {
                set('assignedEmployeePersonalInformationId', "");
                set('employeeData', null);
                set('autoPopulated', {});
              }}
              viewMode={!editable}
              allowClear={true}
              isRequired={true}
              error={!!errors.assignedEmployeePersonalInformationId}
              errorMessage={errors.assignedEmployeePersonalInformationId}
            />
            <EmployeeCard data={form.employeeData} />
          </div>
          <div>
            <EmployeePickerField
              label="Assigned To (Person):"
              employeePersonalInformationId={form.assignedToEmployeePersonalInformationId}
              displayValue={form.assignedToData?.fullName || ""}
              onSelect={(emp) => {
                set('assignedToEmployeePersonalInformationId', emp.id);
                set('assignedToData', { fullName: emp.fullName, employeeId: emp.employeeId, position: emp.employeeInformationPositionDescription || "N/A" });
              }}
              onClear={() => {
                set('assignedToEmployeePersonalInformationId', "");
                set('assignedToData', null);
              }}
              viewMode={!editable}
              allowClear={true}
              isRequired={true}
              error={!!errors.assignedToEmployeePersonalInformationId}
              errorMessage={errors.assignedToEmployeePersonalInformationId}
            />
            <EmployeeCard data={form.assignedToData} />
          </div>
        </div>
      </div>

      {!form?.isOnLeave && (
        <div className="bg-gray-50 p-6 rounded-xl border border-gray-200">
          <SectionBadge label="Employee Medical Clearance" editable={editable} />
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-10 gap-y-4">
            <MedicalClearancePicker
              label="Medical Clearance:"
              medicalClearanceId={form.medicalClearanceId}
              filterReferenceId={form.assignedEmployeePersonalInformationId}
              displayValue={form.medicalClearanceReferenceNumber || "--Select--"}
              onSelect={(m) => {
                set('medicalClearanceId', m.id);
                set('medicalClearanceReferenceNumber', m.referenceNumber);
              }}
              onClear={() => {
                set('medicalClearanceId', "");
                set('medicalClearanceReferenceNumber', "");
              }}
              viewMode={!editable}
              disabled={!form.assignedEmployeePersonalInformationId}
              isRequired={true}
              error={!!errors.medicalClearanceId}
              errorMessage={errors.medicalClearanceId}
            />
          </div>
        </div>
      )}

      <div className="rounded-xl border border-gray-200 bg-gray-50 p-6">
        <div className="space-y-4">
          <SectionBadge label="Employee Info" editable={false} />
          <div className="grid grid-cols-1 gap-x-10 gap-y-4 lg:grid-cols-2">
            <Input label="Employee Type:" value={form.autoPopulated?.employeeType || ""} isDisabled={true} placeholder="-- No Data --" />
            <Input label="Destination:" value={form.autoPopulated?.destination || ""} isDisabled={true} placeholder="-- No Data --" />
            <Input label="Regular Title:" value={form.autoPopulated?.regularTitle || ""} isDisabled={true} placeholder="-- No Data --" />
            <Input label="Regular Vessel:" value={form.autoPopulated?.regularVessel || ""} isDisabled={true} placeholder="-- No Data --" />
            <Input label="Vessel Type:" value={form.autoPopulated?.vesselType || ""} isDisabled={true} placeholder="-- No Data --" />
            <Input label="Via Captain:" value={form.autoPopulated?.viaCaptain || ""} isDisabled={true} placeholder="-- No Data --" />
          </div>
        </div>
      </div>

      <div className="rounded-xl border border-gray-200 bg-gray-50 p-6">
        <div className="space-y-4">
          <SectionBadge label="Temporary Assignment Fields" editable={editable} />

          <div className="grid grid-cols-1 gap-x-10 gap-y-4 lg:grid-cols-2">
            <Select
              label="Temp Employee Type:"
              options={options.employeeTypes}
              value={form.tempEmployeeTypeId}
              onChange={e => set('tempEmployeeTypeId', e.target.value)}
              disabled={!editable}
              placeholder="--Select Temp Employee Type--"
              isRequired={true}
              error={!!errors.tempEmployeeTypeId}
              errorMessage={errors.tempEmployeeTypeId}
            />
            <Select
              label="Classified As:"
              options={options.classifications}
              value={form.classificationId}
              onChange={e => set('classificationId', e.target.value)}
              disabled={!editable}
              placeholder="--Select Classification--"
              isRequired={true}
              error={!!errors.classificationId}
              errorMessage={errors.classificationId}
            />
            <DepartmentPickerField
              label="Unit / Department:"
              departmentId={form.departmentId}
              displayValue={form.departmentName || ""}
              onSelect={(dept) => {
                set('departmentId', dept.id);
                set('departmentName', dept.description);
                handleClearPosition();
              }}
              onClear={() => {
                set('departmentId', ""); set('departmentName', "");
                handleClearPosition();
              }}
              placeholder="--Select Unit / Department--"
              viewMode={!editable}
              allowClear={true}
              isRequired={true}
              error={!!errors.departmentId}
              errorMessage={errors.departmentId}
            />
            <PositionPickerField
              label="Temp Title:"
              positionId={form.tempPositionId}
              departmentId={form.departmentId}
              isDepending={true}
              displayValue={form.tempPositionName || ""}
              onSelect={(pos) => { set('tempPositionId', pos.id); set('tempPositionName', pos.description); }}
              onClear={() => handleClearPosition()}
              placeholder="--Select Temp Title--"
              viewMode={!editable}
              allowClear={true}
              isRequired={true}
              error={!!errors.tempPositionId}
              errorMessage={errors.tempPositionId}
            />
            <VesselPickerField
              label="Temp Vessel:"
              vesselId={form.tempVesselId}
              displayValue={form.tempVesselCode || ""}
              onSelect={(v) => { set('tempVesselId', v.id); set('tempVesselCode', v.code); }}
              onClear={() => { set('tempVesselId', ""); set('tempVesselCode', ""); }}
              viewMode={!editable}
              allowClear={true}
              isRequired={true}
              error={!!errors.tempVesselId}
              errorMessage={errors.tempVesselId}
            />
            <Select
              label="Employment:"
              options={options.employments}
              value={form.employmentId}
              onChange={e => set('employmentId', e.target.value)}
              disabled={!editable}
              isRequired={true}
              error={!!errors.employmentId}
              errorMessage={errors.employmentId}
            />
            <EmployeePickerField
              label="Destination Head:"
              employeePersonalInformationId={form.destinationHeadId}
              displayValue={form.destinationHeadData?.fullName || ""}
              onSelect={(emp) => { set('destinationHeadId', emp.id); set('destinationHeadData', { fullName: emp.fullName, employeeId: emp.employeeId, position: emp.employeeInformationPositionDescription || "N/A" }); }}
              onClear={() => { set('destinationHeadId', ""); set('destinationHeadData', null); }}
              viewMode={!editable}
              allowClear={true}
              isRequired={true}
              error={!!errors.destinationHeadId}
              errorMessage={errors.destinationHeadId}
            />
          </div>
        </div>

      </div>

      {form?.isOnLeave && (
        <div className="bg-gray-50 p-6 rounded-xl border border-gray-200">
          <div className="space-y-4">
            <SectionBadge label="Employee Status Field" editable={editable} />
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-10 gap-y-4">
              <Select
                label="Employee Status:"
                options={options.employeeStatusMaintenance}
                value={form.tempEmployeeStatusId}
                onChange={e => set('tempEmployeeStatusId', e.target.value)}
                disabled={!editable}
                placeholder="--Select Temp Employee Status--"
                isRequired={form?.isOnLeave}
                error={!!errors.tempEmployeeStatusId && form?.isOnLeave}
                errorMessage={form?.isOnLeave ? errors.tempEmployeeStatusId : null}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmployeeInfoTab;